import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const DriverExperienceModal = ({ isOpen, onClose, driverExperience, potentialDriverId, onSave }) => {
    const { isDark } = useTheme();
    const [formData, setFormData] = useState({
        country: '',
        liscence_number: '',
        type: '',
        expiration_date: '',
        potential_driver_id: potentialDriverId || ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        if (driverExperience) {
            setFormData({
                country: driverExperience.country || '',
                liscence_number: driverExperience.liscence_number || '',
                type: driverExperience.type || '',
                expiration_date: driverExperience.expiration_date || '',
                potential_driver_id: driverExperience.potential_driver_id || potentialDriverId || ''
            });
        } else {
            setFormData({
                country: '',
                liscence_number: '',
                type: '',
                expiration_date: '',
                potential_driver_id: potentialDriverId || ''
            });
        }
        setErrors([]);
    }, [driverExperience, potentialDriverId, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);

        try {
            const url = driverExperience
                ? `/api/v1/driver_experiences/${driverExperience.id}`
                : '/api/v1/driver_experiences';

            const method = driverExperience ? 'PATCH' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: JSON.stringify({ driver_experience: formData }),
            });

            const data = await response.json();

            if (response.ok) {
                onSave(data);
                onClose();
            } else {
                setErrors(data.errors || ['An error occurred']);
            }
        } catch (error) {
            console.error('Error saving driver experience:', error);
            setErrors(['Failed to save driver experience']);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        },
        header: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '1.5rem',
        },
        errorContainer: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
        },
        formGroup: {
            marginBottom: '1.5rem',
        },
        label: {
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#d1d5db' : '#374151',
            marginBottom: '0.5rem',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#374151' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '2rem',
        },
        cancelButton: {
            padding: '0.75rem 1.5rem',
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        saveButton: {
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={styles.header}>
                    {driverExperience ? 'Edit Driver Experience' : 'New Driver Experience'}
                </h2>

                {errors.length > 0 && (
                    <div style={styles.errorContainer}>
                        {errors.map((error, index) => (
                            <div key={index}>{error}</div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Country</label>
                        <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="e.g., Ghana, Nigeria, etc."
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>License Number</label>
                        <input
                            type="text"
                            name="liscence_number"
                            value={formData.liscence_number}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Enter license number"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>License Type</label>
                        <input
                            type="text"
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="e.g., Class A, Class B, etc."
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Expiration Date</label>
                        <input
                            type="date"
                            name="expiration_date"
                            value={formData.expiration_date}
                            onChange={handleChange}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={styles.saveButton}
                        >
                            {loading ? 'Saving...' : (driverExperience ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DriverExperienceModal;
